import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useNavigate } from 'react-router-dom'
import apiClient, { clearAuthData, getUserInfoSync, API_ENDPOINTS, BASE_URL } from '../../config/api'
import { isSmallShopMode } from '../../utils/smallShopMode'
import logo from '../../assets/logo.jpeg'

const TopBar = ({ onToggleSidebar, setActiveScreen }) => {
  const navigate = useNavigate()
  const userInfo = getUserInfoSync()
  const companyLogo = userInfo?.company?.logo
  const logoSrc = companyLogo
    ? (companyLogo.startsWith('http') ? companyLogo : `${BASE_URL}${companyLogo}`)
    : logo
  const isMountedRef = useRef(true)
  const [lowStockAlerts, setLowStockAlerts] = useState([])
  const [loadingAlerts, setLoadingAlerts] = useState(false)
  const [alertsError, setAlertsError] = useState(null)

  // Get user display name
  const getUserDisplayName = () => {
    if (userInfo?.employee_name) return userInfo.employee_name
    if (userInfo?.full_name) return userInfo.full_name
    if (userInfo?.first_name && userInfo?.last_name) return `${userInfo.first_name} ${userInfo.last_name}`
    if (userInfo?.username) return userInfo.username
    if (userInfo?.email) return userInfo.email.split('@')[0]
    return 'User'
  }

  const smallShopMode = isSmallShopMode()

  const fetchLowStockAlerts = useCallback(async () => {
    try {
      setLoadingAlerts(true)
      setAlertsError(null)
      const endpoint = smallShopMode ? API_ENDPOINTS.BRANCH_LOW_STOCK : API_ENDPOINTS.LOW_STOCK
      const response = await apiClient.get(endpoint)
      const data = Array.isArray(response.data)
        ? response.data
        : response.data?.results || []

      const alerts = data.filter((item) => {
        const quantity = parseFloat(item.quantity) || 0
        const reorder = parseFloat(item.reorder_level) || 0
        if (reorder === 0) return false
        return quantity < reorder
      })

      if (!isMountedRef.current) return
      setLowStockAlerts(alerts)
    } catch (err) {
      console.error('Error fetching low stock alerts:', err)
      if (!isMountedRef.current) return
      setLowStockAlerts([])
      setAlertsError(err.response?.data?.detail || err.response?.data?.message || 'Failed to fetch low stock alerts.')
    } finally {
      if (!isMountedRef.current) return
      setLoadingAlerts(false)
    }
  }, [smallShopMode])

  useEffect(() => {
    fetchLowStockAlerts()
    const interval = setInterval(fetchLowStockAlerts, 5 * 60 * 1000)
    return () => clearInterval(interval)
  }, [fetchLowStockAlerts])

  useEffect(() => {
    return () => {
      isMountedRef.current = false
    }
  }, [])

  const handleProfileClick = (e) => {
    e.preventDefault()
    if (setActiveScreen) {
      setActiveScreen('profile')
    }
  }

  const handleToggleClick = (e) => {
    e.preventDefault()
    if (onToggleSidebar) {
      onToggleSidebar()
    }
  }

  const handleLogout = (e) => {
    e.preventDefault()
    clearAuthData()
    navigate('/login')
  }

  const handleLowStockNavigate = (e) => {
    e.preventDefault()
    if (setActiveScreen) {
      setActiveScreen('low-stock')
    }
  }

  const formatNumber = (value) => {
    const parsed = parseFloat(value)
    if (Number.isNaN(parsed)) {
      return '0'
    }
    return parsed.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })
  }

  const sortedAlerts = [...lowStockAlerts].sort((a, b) => {
    const aQty = parseFloat(a.quantity) || 0
    const bQty = parseFloat(b.quantity) || 0
    const aReorder = parseFloat(a.reorder_level) || 0
    const bReorder = parseFloat(b.reorder_level) || 0

    if (aQty === 0 && bQty > 0) return -1
    if (aQty > 0 && bQty === 0) return 1

    const aDiff = aQty - aReorder
    const bDiff = bQty - bReorder
    return aDiff - bDiff
  })

  const displayAlerts = sortedAlerts.slice(0, 5)
  const alertCount = lowStockAlerts.length

  return (
    <header id="header" className="header fixed-top d-flex align-items-center">
      <div className="d-flex align-items-center justify-content-between">
        <a href="index.html" className="logo d-flex align-items-center">
          <img src={logoSrc} alt="Logo" />
          <span className="d-none d-lg-block">{userInfo?.company?.name || 'Sell Smart'}</span>
        </a>
        <i className="bi bi-list toggle-sidebar-btn" onClick={handleToggleClick} style={{ cursor: 'pointer' }} />
      </div>{/* End Logo */}

      <nav className="header-nav ms-auto">
        <ul className="d-flex align-items-center">
          <li className="nav-item d-block d-lg-none">
            <a className="nav-link nav-icon search-bar-toggle " href="#">
              <i className="bi bi-search" />
            </a>
          </li>{/* End Search Icon*/}
          <li className="nav-item dropdown">
            <a className="nav-link nav-icon" href="#" data-bs-toggle="dropdown">
              <i className="bi bi-bell" />
              {alertCount > 0 && (
                <span className="badge bg-danger badge-number">{alertCount}</span>
              )}
            </a>{/* End Notification Icon */}
            <ul className="dropdown-menu dropdown-menu-end dropdown-menu-arrow notifications">
              <li className="dropdown-header">
                {loadingAlerts ? (
                  <>Checking stock levels...</>
                ) : alertCount > 0 ? (
                  <>
                    You have {alertCount} low stock alert{alertCount !== 1 ? 's' : ''}
                    <a href="#" onClick={handleLowStockNavigate} style={{ cursor: 'pointer' }}>
                      <span className="badge rounded-pill bg-primary p-2 ms-2">View all</span>
                    </a>
                  </>
                ) : (
                  <>No low stock alerts</>
                )}
              </li>
              {alertsError && !loadingAlerts && (
                <>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li className="notification-item">
                    <i className="bi bi-exclamation-triangle text-warning" />
                    <div>
                      <h4>Unable to load alerts</h4>
                      <p className="small mb-0">{alertsError}</p>
                    </div>
                  </li>
                </>
              )}
              {!alertsError && !loadingAlerts && displayAlerts.length > 0 && (
                <>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  {displayAlerts.map((alert, index) => (
                    <React.Fragment key={`${alert.product_id}-${smallShopMode ? alert.branch_id : alert.warehouse_id}`}>
                      <li className="notification-item" onClick={handleLowStockNavigate} style={{ cursor: 'pointer' }}>
                        <i className="bi bi-exclamation-circle text-warning" />
                        <div>
                          <h4>{alert.product_name || 'Unknown Product'}</h4>
                          <p className="mb-1">
                            {(smallShopMode ? alert.branch_name : alert.warehouse_name) || (smallShopMode ? 'Branch' : 'Warehouse')} • Qty {formatNumber(alert.quantity)} / Reorder {formatNumber(alert.reorder_level)}
                          </p>
                          <p className="text-muted small mb-0">Needs replenishment</p>
                        </div>
                      </li>
                      {index < displayAlerts.length - 1 && (
                        <li>
                          <hr className="dropdown-divider" />
                        </li>
                      )}
                    </React.Fragment>
                  ))}
                </>
              )}
              {!alertsError && !loadingAlerts && alertCount === 0 && (
                <>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li className="notification-item">
                    <i className="bi bi-check-circle text-success" />
                    <div>
                      <h4>All stock levels healthy</h4>
                      <p>No products below reorder level.</p>
                    </div>
                  </li>
                </>
              )}
              {!loadingAlerts && alertCount > 0 && (
                <>
                  <li>
                    <hr className="dropdown-divider" />
                  </li>
                  <li className="dropdown-footer">
                    <a href="#" onClick={handleLowStockNavigate} style={{ cursor: 'pointer' }}>
                      Show all notifications
                    </a>
                  </li>
                </>
              )}
            </ul>{/* End Notification Dropdown Items */}
          </li>{/* End Notification Nav */}

          <li className="nav-item dropdown pe-3">
            <a className="nav-link nav-profile d-flex align-items-center pe-0" href="#" data-bs-toggle="dropdown">
              <img src="assets/img/profile-img.jpg" alt="Profile" className="rounded-circle" />
              <span className="d-none d-md-block dropdown-toggle ps-2">{getUserDisplayName()}</span>
            </a>{/* End Profile Iamge Icon */}
            <ul className="dropdown-menu dropdown-menu-end dropdown-menu-arrow profile">
              <li className="dropdown-header">
                <h6>{getUserDisplayName()}</h6>
                <span>{userInfo?.role_display || 'User'}</span>
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>
              <li>
                <a className="dropdown-item d-flex align-items-center" href="#" onClick={handleProfileClick} style={{ cursor: 'pointer' }}>
                  <i className="bi bi-person" />
                  <span>My Profile</span>
                </a>
              </li>
              <li>
                <hr className="dropdown-divider" />
              </li>
              <li>
                <a className="dropdown-item d-flex align-items-center" href="#" onClick={handleLogout} style={{ cursor: 'pointer' }}>
                  <i className="bi bi-box-arrow-right" />
                  <span>Sign Out</span>
                </a>
              </li>
            </ul>{/* End Profile Dropdown Items */}
          </li>{/* End Profile Nav */}
        </ul>
      </nav>{/* End Icons Navigation */}
    </header>

  )
}

export default TopBar